import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MapPin, Building2 } from "lucide-react";
import { getCities } from "../../services/api";
import "./Navbar.css";
import logo from "../../assets/images/JMJK.png";
import logo1 from "../../assets/images/GlobalAcresLLPFinal.png";

const SITE_NAME = "JMJK Properties";

const PROJECT_CATEGORIES = [
  {
    id: "residential",
    slug: "residential",
    name: "Residential",
    description: "Apartments, villas and gated communities",
  },
  {
    id: "commercial",
    slug: "commercial",
    name: "Commercial",
    description: "Offices, retail and workspaces",
  },
  {
    id: "plots",
    slug: "plots",
    name: "Plots",
    description: "Residential and commercial plots",
  },
];

function cityLinkSlug(c) {
  return c.slug || String(c.name || "").trim().toLowerCase().replace(/\s+/g, "-") || c.id;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    getCities()
      .then((list) => setCities(Array.isArray(list) ? list : []))
      .catch(() => setCities([]));
  }, []);

  return (
    <header
      className={`navbar-modern ${
        scrolled ? "navbar-scrolled" : "navbar-transparent"
      }`}
    >
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src={logo1} alt={SITE_NAME} className="navbar-logo-img" />
        </Link>

        <nav className="navbar-desktop">
          <Link to="/" className="navbar-link gold-underline">
            Home
          </Link>

          <Dropdown label="Projects" icon={Building2}>
            {PROJECT_CATEGORIES.map((c) => (
              <Link
                key={c.id}
                to={`/projects/${c.slug}`}
                className="dropdown-item"
              >
                <span className="dropdown-item-title">{c.name}</span>
                {c.description && (
                  <span className="dropdown-item-desc">{c.description}</span>
                )}
              </Link>
            ))}
          </Dropdown>

          <Dropdown label="City" icon={MapPin}>
            <div className="dropdown-grid">
              {cities.map((c) => (
                <Link
                  key={c.id || c.name}
                  to={`/city/${cityLinkSlug(c)}`}
                  className="dropdown-item"
                >
                  {c.name}
                </Link>
              ))}
              {cities.length === 0 && (
                <span className="dropdown-item">No cities yet</span>
              )}
            </div>
          </Dropdown>

          <Link to="/about" className="navbar-link gold-underline">
            Who We Are
          </Link>
          <Link to="/contact" className="navbar-link gold-underline">
            Contact
          </Link>
        </nav>

        <div className="navbar-actions">
          <Link to="/contact" className="navbar-cta">
            Book a Visit
          </Link>

          <button
            className="navbar-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="navbar-mobile">
          <div className="navbar-mobile-inner">
            <Link to="/" className="mobile-item">
              Home
            </Link>

            <details className="mobile-details">
              <summary className="mobile-item mobile-summary">
                Projects
                <ChevronDown size={16} className="mobile-chevron" />
              </summary>
              <div className="mobile-subs">
                {PROJECT_CATEGORIES.map((c) => (
                  <Link
                    key={c.id}
                    to={`/projects/${c.slug}`}
                    className="mobile-sub"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </details>

            <details className="mobile-details">
              <summary className="mobile-item mobile-summary">
                City
                <ChevronDown size={16} className="mobile-chevron" />
              </summary>
              <div className="mobile-subs">
                {cities.map((c) => (
                  <Link
                    key={c.id || c.name}
                    to={`/city/${cityLinkSlug(c)}`}
                    className="mobile-sub"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </details>

            <Link to="/about" className="mobile-item">
              Who We Are
            </Link>
            <Link to="/contact" className="mobile-item">
              Contact
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Dropdown({ label, icon: Icon, children }) {
  return (
    <div className="nav-dropdown">
      <button type="button" className="nav-dropdown-trigger">
        {Icon && <Icon size={16} />}
        <span>{label}</span>
        <ChevronDown size={14} className="nav-dropdown-chevron" />
      </button>
      <div className="nav-dropdown-panel">
        <div className="nav-dropdown-inner">{children}</div>
      </div>
    </div>
  );
}